/**
 * GlobalChatDock — the ONE conversational agent, available on EVERY
 * screen, CONTEXTUAL to where the user is (2026-05-18b · FIX 3+4).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY
 * ─────────────────────────────────────────────────────────────────────────
 *  Before this, chat only existed INSIDE a project ("Project Conversation",
 *  a fixed top-right label) and was ABSENT on admin Home and every other
 *  management screen. Owner feedback: chat must be everywhere and must know
 *  which screen the user is on.
 *
 *  This dock:
 *   - mounts once in AppShell → present on Home, Lead Inbox, project,
 *     ancillary, money, workload, … every authenticated screen.
 *   - derives a screen SCOPE + human LABEL + entity id from the current
 *     route and passes them into the SAME agent (avbpAgentChat via
 *     ChatSurface agentMode) so it answers/acts about THIS screen.
 *   - reuses ChatSurface (generalised from project-only to screen-context)
 *     — no second chat implementation.
 *   - honours the conversation visibility + role model already enforced
 *     server-side (admin/staff full · client safe subset): `isTeamView`
 *     resolves from the locked role model exactly as the Project
 *     Conversation does.
 *   - keeps the ⌘K shortcut (now: toggle this dock).
 *
 *  Pure-white surface; borders + type only (owner-locked).
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { visibilityFor } from '../lib/roles';
import { screenContextFor } from '../lib/screenContext';
import ChatSurface from './ChatSurface';

export { screenContextFor };

export default function GlobalChatDock() {
  const { role } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const ctx = useMemo(
    () => screenContextFor(loc.pathname),
    [loc.pathname],
  );

  // SAME locked role model the Project Conversation uses: admin/staff see
  // the team layer; client gets the safe subset (also enforced server-side
  // in avbpAgentChat). One rule, applied everywhere.
  const isTeamView = visibilityFor(role).seesReviewLayer;

  // "Forward to client" is only meaningful when a specific client or project
  // is in context (AD-005, Fix 4). On global/list screens (Contacts, Leads,
  // Team Inbox, etc.) the button is hidden — there is no single client to
  // forward to, so showing it is misleading.
  const canForwardToClient = ctx.screen === 'project' || ctx.screen === 'conversation';

  // ⌘K toggles the dock (keep the shortcut). Esc closes.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // The label is CONTEXTUAL — never a hardcoded "Project Conversation".
  const headerTitle =
    ctx.screen === 'project'
      ? 'Project assistant'
      : `Assistant · ${ctx.screenLabel}`;
  const triggerLabel =
    ctx.screen === 'home'
      ? 'Ask about Home'
      : `Ask about ${ctx.screenLabel}`;

  return (
    <>
      {/* context-aware affordance (replaces the fixed "Project
          Conversation" CmdK trigger; ⌘K still works) */}
      <button
        className="cmdk-trigger gdock-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerLabel}
        title={`${triggerLabel} (⌘K)`}
      >
        <span aria-hidden>◧</span>
        <span>{triggerLabel}</span>
        <kbd>⌘K</kbd>
      </button>

      {open && (
        <div className="gdock" role="dialog" aria-label={headerTitle}>
          <div className="gdock-bar">
            <span className="gdock-where">
              {ctx.screen === 'project' && ctx.screenEntityId
                ? `Project · ${ctx.screenEntityId}`
                : ctx.screenLabel}
            </span>
            <button
              className="gdock-x"
              onClick={() => setOpen(false)}
              aria-label="Close assistant"
              title="Close (Esc)"
            >
              ✕
            </button>
          </div>
          <ChatSurface
            key={`${ctx.screen}:${ctx.screenEntityId ?? ''}`}
            title={headerTitle}
            note={
              isTeamView
                ? 'Ask or act about this screen — the assistant can take real actions.'
                : 'Ask about this screen — the team sees anything you raise.'
            }
            authorName={isTeamView ? 'You' : 'You'}
            isTeamView={isTeamView}
            agentMode
            docked
            screenCtx={ctx}
            projectId={
              ctx.screen === 'project' ? ctx.screenEntityId : undefined
            }
            canForwardToClient={canForwardToClient}
            onNavigate={(route) => {
              setOpen(false);
              navigate(route);
            }}
            onApply={() => {
              /* agent mode produces no chips — nothing to apply */
            }}
          />
        </div>
      )}
    </>
  );
}
